/**
 * NotificationRoadmapItem
 *
 * Covers the six roadmap-related notification types behind one component
 * varying only by `kind` (they share the same skeleton: tappable avatar,
 * `Total: {countText} - {date}` subtitle, and a fixed trailing `roadmap.png`
 * icon — NOT a media thumbnail). Ports:
 *   - roadmapLike         — `notification_roadmap_like_item.dart:26-103`
 *   - roadmapDislike      — `notification_roadmap_dislike_item.dart:26-104`
 *   - roadmapComment      — `notification_roadmap_comment_item.dart:26-105`
 *   - roadmapCommentReply — `notification_roadmap_comment_reply_item.dart:26-106`
 *   - roadmapCommentLike  — `notification_roadmap_comment_like_item.dart:26-111` (undo-aware title)
 *   - roadmapDonation     — `notification_roadmap_donation_item.dart:26-98` (USD total, no pluralization)
 */
import React from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { MathUtils } from '@/domain/utils/mathUtils';
import { NotificationRoadmapKind } from '@/domain/enums/inbox/notificationRoadmapKind';
import { NotificationRowLayout } from '../NotificationRowLayout';
import { NotificationAvatar } from './shared/NotificationAvatar';
import { NotificationLeadingIcon } from './shared/NotificationLeadingIcon';
import type { NotificationItemProps } from './notificationItemProps';

interface NotificationRoadmapItemProps extends NotificationItemProps {
  kind: NotificationRoadmapKind;
}

export const NotificationRoadmapItem: React.FC<NotificationRoadmapItemProps> = ({
  kind,
  notification,
  onUserTap,
  onItemTap,
}) => {
  const { l10n } = useLocale();
  const { formatDate } = useDateFormatters();
  const { theme } = useTheme();
  const n = l10n.inbox.notifications;

  const { info, stats } = notification;
  const name = info.usersInfo.name;
  const isUndo = info.isUndo === true;

  // Title (undo-aware only for roadmapCommentLike).
  let title: string;
  switch (kind) {
    case NotificationRoadmapKind.roadmapLike:
      title = n.roadmapLiked;
      break;
    case NotificationRoadmapKind.roadmapDislike:
      title = n.roadmapDisliked;
      break;
    case NotificationRoadmapKind.roadmapComment:
      title = n.roadmapCommented;
      break;
    case NotificationRoadmapKind.roadmapCommentReply:
      title = n.roadmapCommentReplied;
      break;
    case NotificationRoadmapKind.roadmapCommentLike:
      title = isUndo ? n.roadmapCommentUnliked : n.roadmapCommentLiked;
      break;
    case NotificationRoadmapKind.roadmapDonation:
      title = n.roadmapDonated;
      break;
  }
  title = title.replace('{name}', name);

  // Count text feeding the "Total: {value}" subtitle.
  const countText = (() => {
    if (kind === NotificationRoadmapKind.roadmapDonation) {
      return n.donationUsd.replace(
        '{value}',
        MathUtils.getReactionsIntlNumber(stats?.donations ?? 0),
      );
    }

    let count: number;
    let zero: string;
    let one: string;
    let many: string;
    switch (kind) {
      case NotificationRoadmapKind.roadmapLike:
      case NotificationRoadmapKind.roadmapCommentLike:
        count =
          (kind === NotificationRoadmapKind.roadmapLike
            ? stats?.contentLikes
            : stats?.commentLikes) ?? 0;
        zero = n.zeroLikes;
        one = n.oneLike;
        many = n.manyLikes;
        break;
      case NotificationRoadmapKind.roadmapDislike:
        count = stats?.contentLikes ?? 0;
        zero = n.zeroDislikes;
        one = n.oneDislike;
        many = n.manyDislikes;
        break;
      case NotificationRoadmapKind.roadmapComment:
        count = stats?.contentComments ?? 0;
        zero = n.zeroComments;
        one = n.oneComment;
        many = n.manyComments;
        break;
      case NotificationRoadmapKind.roadmapCommentReply:
        count = stats?.commentReplies ?? 0;
        zero = n.zeroReplies;
        one = n.oneReply;
        many = n.manyReplies;
        break;
    }
    if (count === 0) return zero;
    if (count === 1) return one;
    return many.replace('{count}', MathUtils.getReactionsIntlNumber(count));
  })();

  const subtitle = n.totalSubtitle
    .replace('{value}', countText)
    .replace('{date}', formatDate(info.timestamp));

  return (
    <NotificationRowLayout
      leading={<NotificationAvatar userId={info.usersInfo.userId} alt={name} />}
      title={title}
      subtitle={subtitle}
      trailing={
        <NotificationLeadingIcon
          iconPath="/assets/images/roadmap.png"
          iconColor={theme.white}
        />
      }
      onItemTap={onItemTap ? () => onItemTap(notification) : undefined}
      onLeadingTap={onUserTap ? () => onUserTap(info.usersInfo.userId) : undefined}
    />
  );
};
